import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { apiClient, readError } from "@/lib/api";
import { fmtEUR, StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  ArrowLeft, Users, Briefcase, Wallet, Scale, AlertTriangle,
  Mail, Phone, MapPin, Building2, ExternalLink, Layers, ShieldCheck,
  TrendingUp,
} from "lucide-react";

const DEMANDEUR_TYPE_LABEL = {
  particulier: "Particulier",
  architecte: "Architecte",
  promoteur: "Promoteur immobilier",
  cmi: "Constructeur (CMI)",
  syndic: "Syndic de copropriété",
  bailleur: "Bailleur / Foncière",
  entreprise_btp: "Entreprise BTP",
  collectivite: "Collectivité",
  maitre_oeuvre: "Maître d'œuvre",
  investisseur: "Investisseur locatif",
};

const STATUS_LABELS = {
  resolved: { label: "Résolu (accord)", color: "bg-emerald-50 text-emerald-800 border-emerald-400" },
  expired: { label: "Expiré 30j+", color: "bg-zinc-100 text-zinc-700 border-zinc-400" },
  mediation: { label: "En médiation", color: "bg-amber-50 text-amber-800 border-amber-400" },
  open: { label: "Ouvert", color: "bg-[#FF4D00] text-white border-[#FF4D00]" },
  closed_admin: { label: "Clos admin", color: "bg-zinc-100 text-zinc-700 border-zinc-400" },
};

export default function AdminDemandeurDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [disputes, setDisputes] = useState(null);

  useEffect(() => {
    Promise.all([
      apiClient.get(`/admin/demandeurs/${id}`),
      apiClient.get(`/admin/demandeurs/${id}/disputes`),
    ])
      .then(([{ data: d }, { data: dp }]) => { setData(d); setDisputes(dp); })
      .catch((e) => toast.error(readError(e)));
  }, [id]);

  if (!data) return (
    <div className="min-h-screen bg-[#F7F7F9]">
      <Navbar />
      <div className="p-10 mono text-sm">Chargement…</div>
    </div>
  );

  const { profile, missions, chantiers, stats } = data;
  const totalDisputes = disputes?.total || 0;
  const riskLevel = totalDisputes >= 3 ? "high" : totalDisputes >= 1 ? "medium" : "low";

  return (
    <div className="min-h-screen bg-[#F7F7F9]" data-testid="admin-demandeur-detail-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <Link to="/admin" className="mono text-xs uppercase tracking-widest text-zinc-500 hover:text-[#FF4D00] inline-flex items-center gap-1" data-testid="back-to-admin">
          <ArrowLeft className="w-3.5 h-3.5" /> Retour console admin
        </Link>

        {/* HEADER */}
        <div className="mt-6 border-2 border-[#0A0A0B] bg-white shadow-[8px_8px_0_#FF4D00]">
          <div className="bg-[#0A0A0B] text-white px-6 py-5 flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">// Fiche demandeur</div>
              <div className="display text-3xl mt-2">{profile.name || "—"}</div>
              <div className="text-sm text-zinc-400 mt-1">{profile.email}</div>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="badge-pill bg-white text-[#0A0A0B] border-white">
                  {DEMANDEUR_TYPE_LABEL[profile.demandeur_type] || "Particulier"}
                </span>
                {profile.terms_accepted_at && (
                  <span className="badge-pill bg-emerald-50 text-emerald-800 border-emerald-400">
                    RGPD tracé {profile.terms_accepted_at.slice(0, 10)}
                  </span>
                )}
                {riskLevel === "high" && (
                  <span className="badge-pill bg-red-600 text-white border-red-700">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                    Risque élevé ({totalDisputes} litiges)
                  </span>
                )}
                {riskLevel === "medium" && (
                  <span className="badge-pill bg-amber-100 text-amber-800 border-amber-400">
                    {totalDisputes} litige{totalDisputes > 1 ? "s" : ""} historique
                  </span>
                )}
                {riskLevel === "low" && (
                  <span className="badge-pill bg-emerald-50 text-emerald-800 border-emerald-400">
                    ✓ Sans litige
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-400">Volume total</div>
              <div className="display text-5xl text-[#FF4D00]">{fmtEUR(stats.total_spent)}</div>
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                depuis {(profile.created_at || "").slice(0, 10) || "—"}
              </div>
            </div>
          </div>

          {/* Identity */}
          <div className="p-6 grid md:grid-cols-4 gap-3 border-b-2 border-[#0A0A0B]">
            <Mini icon={Phone} label="Téléphone" value={profile.phone || "—"} mono />
            <Mini icon={MapPin} label="Ville" value={profile.city || "—"} />
            {profile.company && <Mini icon={Building2} label="Entreprise" value={profile.company} />}
            {profile.siret && <Mini label="SIRET" value={profile.siret} mono />}
            <Mini label="Version CGU acceptée" value={profile.terms_version || "—"} mono />
            <Mini icon={Mail} label="Email" value={profile.email} />
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-b-2 border-[#0A0A0B]">
            <KPI icon={Briefcase} label="Missions" value={stats.missions_count} sub={`${stats.missions_count} au total`} />
            <KPI icon={Layers} label="Chantiers multi-lot" value={stats.chantiers_count} />
            <KPI icon={Wallet} label="Total dépensé" value={fmtEUR(stats.total_spent)} accent />
            <KPI icon={Scale} label="Litiges" value={totalDisputes} sub={`${disputes?.counts?.resolved || 0} résolus · ${disputes?.counts?.expired || 0} expirés`} danger={riskLevel === "high"} />
          </div>
        </div>

        {/* DISPUTES */}
        {totalDisputes > 0 && (
          <div className="mt-8 border-2 border-[#0A0A0B] bg-white">
            <div className="bg-[#0A0A0B] text-white px-5 py-3">
              <div className="mono text-[11px] uppercase tracking-widest">// Historique litiges ({totalDisputes})</div>
            </div>
            <div className="p-5 space-y-3">
              {(disputes?.disputes || []).slice(0, 10).map((d) => {
                const st = STATUS_LABELS[d.status] || { label: d.status, color: "bg-white border-zinc-300" };
                return (
                  <div key={d.id} className="border-2 border-zinc-200 p-3 flex flex-wrap items-center justify-between gap-3" data-testid={`demandeur-dispute-${d.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`badge-pill ${st.color}`}>{st.label}</span>
                        <strong>{d.mission_title || "—"}</strong>
                        <span className="text-xs text-zinc-500">· {fmtEUR(d.amount_escrow)}</span>
                      </div>
                      <div className="text-xs text-zinc-600 mt-1 italic">&quot;{(d.reason || "").slice(0, 180)}{d.reason?.length > 180 ? "…" : ""}&quot;</div>
                    </div>
                    <div className="text-right shrink-0 mono text-[10px] uppercase tracking-widest text-zinc-500">
                      {(d.created_at || "").slice(0, 10)}
                      {d.resolved_at && <> → {d.resolved_at.slice(0, 10)}</>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* MISSIONS */}
        <div className="mt-8 border-2 border-[#0A0A0B] bg-white">
          <div className="bg-[#FF4D00] text-white px-5 py-3">
            <div className="mono text-[11px] uppercase tracking-widest">// Projets missions ({missions.length})</div>
          </div>
          {missions.length === 0 ? (
            <div className="p-8 text-center mono text-xs text-zinc-500">Aucune mission publiée.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-100">
                  <tr>
                    <th className="text-left px-4 py-3 mono text-[10px] uppercase tracking-widest">Titre</th>
                    <th className="text-left px-4 py-3 mono text-[10px] uppercase tracking-widest">Catégorie · Ville</th>
                    <th className="text-left px-4 py-3 mono text-[10px] uppercase tracking-widest">Artisan sélectionné</th>
                    <th className="text-right px-4 py-3 mono text-[10px] uppercase tracking-widest">Accepté</th>
                    <th className="text-center px-4 py-3 mono text-[10px] uppercase tracking-widest">Statut</th>
                    <th className="text-left px-4 py-3 mono text-[10px] uppercase tracking-widest">Créée</th>
                    <th className="text-center px-4 py-3 mono text-[10px] uppercase tracking-widest">Ouvrir</th>
                  </tr>
                </thead>
                <tbody>
                  {missions.map((m) => (
                    <tr key={m.id} className="border-t border-zinc-200" data-testid={`demandeur-mission-${m.id}`}>
                      <td className="px-4 py-3 font-medium">{m.title}</td>
                      <td className="px-4 py-3 text-xs">{m.category} · {m.city}</td>
                      <td className="px-4 py-3 text-xs">
                        {m.selected_artisan_name || "—"}
                        {m.selected_artisan_company ? <div className="text-zinc-500">{m.selected_artisan_company}</div> : null}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">{m.accepted_amount ? fmtEUR(m.accepted_amount) : "—"}</td>
                      <td className="px-4 py-3 text-center"><StatusBadge status={m.status} /></td>
                      <td className="px-4 py-3 text-xs text-zinc-500">{(m.created_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-3 text-center">
                        <Link to={`/missions/${m.id}`} className="text-[#FF4D00] hover:underline" data-testid={`goto-mission-${m.id}`}>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* CHANTIERS */}
        {chantiers && chantiers.length > 0 && (
          <div className="mt-8 border-2 border-[#0A0A0B] bg-white">
            <div className="bg-amber-500 text-[#0A0A0B] px-5 py-3">
              <div className="mono text-[11px] uppercase tracking-widest">// Chantiers multi-lot ({chantiers.length})</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-100">
                  <tr>
                    <th className="text-left px-4 py-3 mono text-[10px] uppercase tracking-widest">Titre</th>
                    <th className="text-left px-4 py-3 mono text-[10px] uppercase tracking-widest">Ville</th>
                    <th className="text-center px-4 py-3 mono text-[10px] uppercase tracking-widest">Lots</th>
                    <th className="text-left px-4 py-3 mono text-[10px] uppercase tracking-widest">Créé</th>
                  </tr>
                </thead>
                <tbody>
                  {chantiers.map((c) => (
                    <tr key={c.id} className="border-t border-zinc-200" data-testid={`demandeur-chantier-${c.id}`}>
                      <td className="px-4 py-3 font-medium">{c.title || "—"}</td>
                      <td className="px-4 py-3 text-xs">{c.city || "—"}</td>
                      <td className="px-4 py-3 text-center">{(c.lots || []).length}</td>
                      <td className="px-4 py-3 text-xs text-zinc-500">{(c.created_at || "").slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Mini({ icon: Icon, label, value, mono }) {
  return (
    <div className="border border-zinc-200 p-3">
      <div className="mono text-[9px] uppercase tracking-widest text-zinc-400 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className={`text-sm mt-1 ${mono ? "mono" : ""}`}>{value}</div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, accent, danger }) {
  const bg = danger ? "bg-red-600 text-white" : accent ? "bg-[#FF4D00] text-white" : "bg-white";
  const iconColor = danger || accent ? "text-white" : "text-[#FF4D00]";
  const subColor = danger || accent ? "text-white/80" : "text-zinc-500";
  return (
    <div className={`p-6 border-r-2 last:border-r-0 border-[#0A0A0B] ${bg}`}>
      <Icon className={`w-6 h-6 ${iconColor}`} />
      <div className={`mono text-[10px] uppercase tracking-widest mt-3 ${subColor}`}>{label}</div>
      <div className="display text-3xl mt-1">{value}</div>
      {sub && <div className={`mono text-[10px] uppercase tracking-widest mt-1 ${subColor}`}>{sub}</div>}
    </div>
  );
}
